import { useMediaQuery } from "@mui/material";
import { useIntersectionObserver, useIsClient } from "@uidotdev/usehooks";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  bookmarkDiscoverCompany,
  unBookmarkDiscoverCompany,
  updateSelectedCompany,
} from "../../../redux/actions/company_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
} from "../../jobs/AccordionJobCard/styles";
import {
  ArrowLeftIconStyled,
  ArrowRightIconStyled,
  BookmarkFilledIconStyled,
  BookmarkIconStyled,
  ButtonStyled,
  CircularProgressStyled,
  CompanyLogo,
  FlexContainer,
  HScrollContainer,
  IndustryIconStyled,
  JobIconStyled,
  LinkWrapper,
  SectionContainer,
  TextStyled,
} from "../styles";
import { CardStyled, CompanyBanner, OutlineButton } from "./styles";

function DiscoverCompany(props) {
  const { companyDetail } = props || {};
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isMobile = useMediaQuery("(max-width: 320px)");
  const isTablet = useMediaQuery("(min-width: 321px) and (max-width: 1279px)");

  const discoverCompanies = useSelector(
    (state) => state.companies.cpDiscoverMoreCompanies
  );
  const companyList = discoverCompanies?.filter(
    (company) => company?.company?.id !== companyDetail?.id
  );

  const isClient = useIsClient();
  const [refLeft, entryLeft] = useIntersectionObserver();
  const [refRight, entryRight] = useIntersectionObserver({
    threshold: 0.925,
    root: null,
    rootMargin: "0px",
  });

  const lastFilterItem = useMemo(() => companyList.length - 1, [companyList]);

  const scrollContainerRef = useRef(null);

  function handleScroll(direction) {
    const scrollAmount = 983;
    const container = scrollContainerRef.current;

    if (container) {
      const currentScrollLeft = container.scrollLeft;
      let targetScrollLeft;

      if (direction === "left") {
        sendTrackingEvent({
          event: "CE_click-previous-companies-cdp",
        });
        targetScrollLeft = currentScrollLeft - scrollAmount;
      } else if (direction === "right") {
        sendTrackingEvent({
          event: "CE_click-next-companies-cdp",
        });
        targetScrollLeft = currentScrollLeft + scrollAmount;
      }

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth", // Add smooth scrolling behavior
      });
    }
  }

  function handleEventViewMoreCompanies() {
    sendTrackingEvent({
      event: "CE_click-view-more-companies-cdp",
    });
  }

  // Determine gap based on screen size
  const getGap = () => {
    if (isMobile) return "6rem"; // 320px and below
    if (isTablet) return "2rem"; // 321px - 1279px
    return "0.5rem"; // 1280px and above (desktop)
  };

  return isClient ? (
    <SectionContainer aria-label={t("companydetail.discover_section_aria")}>
      <TextStyled
        variant="h2"
        fontSize="1.25rem"
        fontWeight={700}
        textTransform="capitalize"
        aria-label={t("companydetail.discover_section_title_aria")}
        color={"#000"}
      >
        {t("companydetail.discover_companies_title")}
      </TextStyled>
      <HScrollContainer
        direction="row"
        gap={getGap()}
        marginTop="1rem"
        ref={scrollContainerRef}
      >
        {companyList.map((company, index) => {
          return (
            <CompanyCard
              key={index}
              index={index}
              company={company?.company}
              refRight={refRight}
              refLeft={refLeft}
              lastFilterItem={lastFilterItem}
              companyListLength={companyList?.length}
            />
          );
        })}
      </HScrollContainer>
      <FlexContainer
        alignItems="center"
        justifyContent="space-between"
        marginTop="1rem"
        width={"100%"}
      >
        <OutlineButton
          href={`/companies?industry_ids=${companyDetail?.industryId}`}
          rel="noopener noreferrer"
          target="_blank"
          onClick={handleEventViewMoreCompanies}
          id="view-more-companies"
        >
          {t("companydetail.view_more_companies")}
        </OutlineButton>{" "}
        {isDesktop && companyList?.length > 3 ? (
          <FlexContainer gap="1rem" sx={{ width: "auto" }}>
            <ButtonStyled
              onClick={() => handleScroll("left")}
              sx={entryLeft?.isIntersecting && { display: "none" }}
              id="prev-disc-company-list"
            >
              <ArrowLeftIconStyled />
            </ButtonStyled>

            <ButtonStyled
              onClick={() => handleScroll("right")}
              disabled={entryRight?.isIntersecting}
              id="next-disc-company-list"
            >
              <ArrowRightIconStyled />
            </ButtonStyled>
          </FlexContainer>
        ) : null}
      </FlexContainer>
    </SectionContainer>
  ) : null;
}

function CompanyCard(props) {
  const {
    index,
    company,
    companyListLength,
    refLeft,
    refRight,
    lastFilterItem,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const industries = useSelector((state) => state.companies.industries);
  const [targetCompany, setTargetCompany] = useState(null);

  const bookmarking = useSelector(
    (state) => state.companies.bookmarkingDiscoverCompany
  );
  const unbookmarking = useSelector(
    (state) => state.companies.unbookmarkDiscoverCompany
  );

  async function handleBookmarkCompany(e, companyId) {
    e.preventDefault();
    e.stopPropagation();

    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      return dispatch(updateSelectedCompany(company)).then(() => {
        dispatch(updateSignUpOrigin("save_company")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    setTargetCompany(companyId);

    if (!company?.bookmark) {
      dispatch(bookmarkDiscoverCompany({ companyId: companyId }));

      sendTrackingEvent({
        event: "CE_follow-company-card-cdp",
        "company-id": companyId,
      });

      toast.success(t("companydetail.company_bookmarked"));
      setTargetCompany(null);
    } else if (company?.bookmark) {
      dispatch(unBookmarkDiscoverCompany({ companyId: companyId }));

      sendTrackingEvent({
        event: "CE_unfollow-company-card-cdp",
        "company-id": companyId,
      });

      toast.success(t("companydetail.company_unbookmarked"));
      setTargetCompany(null);
    }
  }

  const industryLabel = useMemo(() => {
    if (!company?.industry && !company?.industryId) return "";
    const keyBase = company?.industry;
    if (!keyBase) return "";
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.industry_labels.${key}`);
  }, [company?.industry, company?.industryId, industries, t]);

  function handleEventCompanyCard(company) {
    sendTrackingEvent({
      event: "CE_click-company-cdp",
      "company-id": company?.id,
    });
  }

  const isCompanyVerified =
    company?.verified === true ||
    company?.name === "Hiredly" ||
    company?.name === "Hiredly X";

  return (
    <LinkWrapper
      href={"/companies/" + company?.slug}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => handleEventCompanyCard(company)}
    >
      <CardStyled
        sx={{ width: companyListLength > 3 ? "20rem" : "100%" }}
        ref={index === 0 ? refLeft : index === lastFilterItem ? refRight : null}
      >
        <CompanyBanner
          style={{
            backgroundImage: `url(${company?.coverImageThumb})`,
          }}
        >
          <FlexContainer
            flexDirection={"column"}
            alignItems="flex-end"
            justifyContent="space-between"
            height="100%"
            padding="0.75rem"
          >
            {(bookmarking || unbookmarking) && targetCompany === company?.id ? (
              <CircularProgressStyled />
            ) : company?.bookmark ? (
              <BookmarkFilledIconStyled
                onClick={(e) => handleBookmarkCompany(e, company?.id)}
                width="2rem"
                height="2rem"
                stroke={Color.black}
                id="disc-company-bookmark"
              />
            ) : (
              <BookmarkIconStyled
                onClick={(e) => handleBookmarkCompany(e, company?.id)}
                width="2rem"
                height="2rem"
                stroke={Color.black}
                id="disc-company-bookmark"
              />
            )}

            <CompanyLogo
              src={company?.logo}
              loading="lazy"
              width={80}
              height={80}
              alt={t("companydetail.company_logo_alt", {
                company: company?.name,
              })}
              aria-label={t("companydetail.company_logo_aria")}
              id="disc-company-logo-big"
              style={{ border: `1px solid ${Color.black}` }}
            />
          </FlexContainer>
        </CompanyBanner>
        <FlexContainer
          flexDirection={"column"}
          padding="0.75rem 1rem 1rem 1rem"
          gap="0.5rem"
        >
          <FlexContainer gap={"1rem"} sx={{ minWidth: 0 }}>
            <CompanyLogo
              src={company?.logo}
              loading="lazy"
              width={24}
              height={24}
              alt={t("companydetail.company_logo_alt", {
                company: company?.name,
              })}
              aria-label={t("companydetail.company_logo_aria")}
              id="disc-company-logo-small"
              style={{
                border: `0.5px solid ${Color.black}`,
                borderRadius: "0.25rem",
              }}
            />

            <CompanyNameRow
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <TextStyled
                variant="h3"
                fontSize="1.125rem"
                fontWeight={700}
                fontFamily={"Karla"}
                letterSpacing={"0.27px"}
                whiteSpace={"nowrap"}
                overflow={"hidden"}
                textOverflow={"ellipsis"}
                sx={{ flex: "0 1 auto", minWidth: 0, display: "block" }}
                id="disc-company-name"
              >
                {company?.name}
              </TextStyled>
              {isCompanyVerified && (
                <CompanyVerifiedBadge aria-label="Verified company" />
              )}
            </CompanyNameRow>
          </FlexContainer>
          <FlexContainer flexDirection={"column"} gap="0.5rem">
            <FlexContainer
              alignItems="center"
              gap={"0.5rem"}
              textTransform="capitalize"
              id="disc-company-industry"
            >
              <IndustryIconStyled />

              <TextStyled
                variant="h4"
                fontSize="0.875rem"
                fontWeight={400}
                fontFamily={"Karla"}
                letterSpacing={"0.15px"}
              >
                {industryLabel}
              </TextStyled>
            </FlexContainer>

            <FlexContainer
              alignItems="center"
              gap={"0.5rem"}
              id="disc-company-job-count"
            >
              <JobIconStyled />
              <TextStyled
                variant="h4"
                fontSize="0.875rem"
                fontWeight={400}
                fontFamily={"Karla"}
                letterSpacing={"0.15px"}
              >
                {t("companydetail.jobs_count", {
                  count: company?.jobCount,
                })}
              </TextStyled>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </CardStyled>
    </LinkWrapper>
  );
}

export default DiscoverCompany;
